import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getOrderNetAmount, getOrderNetAmounts } from '@/api/orders';
import { useOrderWorkspaceSession, boundedOrderRead } from '@/hooks/useOrderWorkspaceSession';

export const netInput = order => ({ total_price: String(order?.total_price ?? ''),
  network_source: String(order?.network_source ?? ''), delivery_date: String(order?.delivery_date ?? '') });
const sameInput = (a, b) => JSON.stringify(netInput(a)) === JSON.stringify(netInput(b));
const valid = row => row && ['ready', 'not_configured', 'unavailable'].includes(row.status)
  && (row.status !== 'ready' || (['gross_cents', 'source_cents', 'commission_cents', 'net_cents']
    .every(key => Number.isSafeInteger(row[key])) && row.commission_cents >= 0
    && row.net_cents === row.gross_cents - row.source_cents - row.commission_cents
    && typeof row.source_configured === 'boolean'));

// Read observations only: no shared financial cache, write lock or mutation retry.
export function useOrderNetAmounts(orders, { draft = false, enabled = true, refreshKey = null } = {}) {
  const session = useOrderWorkspaceSession();
  const { sessionBinding } = useAuth() || {};
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState(null);
  const key = JSON.stringify(orders.map(order => [order.id, netInput(order), order.desktop_sync?.origin === 'local']));
  const allowed = enabled && sessionBinding?.source === 'cloud' && !session.offline;
  const owner = useMemo(() => ({ active: false, key, sessionKey: session.key, allowed, draft, refreshKey, retry }),
    [key, session.key, allowed, draft, refreshKey, retry]);
  const latest = useRef(null); latest.current = { owner, session };
  useEffect(() => {
    owner.active = true;
    const attempt = {}; owner.attempt = attempt;
    const controllers = new Set();
    const captured = allowed && orders.some(order => order.desktop_sync?.origin !== 'local')
      ? latest.current.session.capture(false) : null;
    owner.captured = captured;
    const owns = () => owner.active && owner.attempt === attempt
      && latest.current.owner === owner && captured?.current();
    const rows = orders.filter(order => order.desktop_sync?.origin !== 'local');
    if (!allowed || !captured) {
      setResult({ owner, status: 'unavailable', rows: {}, reason: 'offline_or_access' });
    } else {
      setResult({ owner, status: 'loading', rows: {} });
      (async () => {
        const received = {};
        for (let start = 0; start < rows.length; start += 100) {
          if (!owns()) return;
          const chunk = rows.slice(start, start + 100);
          const data = await boundedOrderRead(config => draft
            ? getOrderNetAmount(chunk[0].id, netInput(chunk[0]), config).then(row => ({ orders: [row] }))
            : getOrderNetAmounts(chunk.map(order => order.id), config), captured.config, controllers);
          if (!owns()) return;
          if (!Array.isArray(data?.orders) || data.orders.length !== chunk.length
            || new Set(data.orders.map(row => row?.id)).size !== chunk.length) throw new Error('Invalid NET response');
          for (const order of chunk) {
            const row = data.orders.find(item => item?.id === order.id);
            if (!valid(row)) throw new Error('Invalid NET amount');
            received[order.id] = sameInput(order, row.input) ? row : { status: 'unavailable', reason: 'order_changed' };
          }
        }
        if (owns()) setResult({ owner, status: 'ready', rows: received });
      })().catch(() => { if (owns()) setResult({ owner, status: 'unavailable', rows: {} }); });
    }
    return () => { owner.active = false; controllers.forEach(controller => controller.abort()); };
    // The serialized key captures the complete input; callback/allocation identities do not reread.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);
  const current = result?.owner === owner && (!allowed || owner.captured?.current() || result.status === 'unavailable')
    ? result : { status: allowed && !owner.captured ? 'loading' : 'unavailable', rows: {} };
  return { ...current, forOrder: order => order.desktop_sync?.origin === 'local'
    ? { status: 'unavailable', reason: 'local_order' }
    : current.rows[order.id] || { status: current.status === 'loading' ? 'loading' : 'unavailable', reason: current.reason },
  retry: () => setRetry(value => value + 1) };
}
